import React, { useState, useEffect } from "react";
import { useParams, useSearchParams, Link } from "react-router-dom";
import Logo from '../images/school_logo.png';
import Loader from "./Loader";
import SuccessModal from './SuccessModal';
import { db } from '../config/fbConfig';
import { collection, getDocs, query, where, doc, getDoc, writeBatch } from 'firebase/firestore';

const PsychomotorSkills = () => {
  const { classId } = useParams();
  const [searchParams] = useSearchParams();
  
  const [students, setStudents] = useState([]);
  const [classInfo, setClassInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editableSkills, setEditableSkills] = useState({});
  const [isDirty, setIsDirty] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [error, setError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [saving, setSaving] = useState(false);

  // List of psychomotor skills to assess
  const psychomotorSkillsList = [
    'handwriting',
    'fluency',
    'games',
    'punctuality',
    'neatness',
    'politeness',
    'cooperation',
    'leadership',
    'attentiveness',
    'honesty'
  ];

  useEffect(() => {
    const fetchData = async () => {
      try {
        const classDoc = await getDoc(doc(db, 'classes', classId));
        setClassInfo(classDoc.data());
  
        const studentIds = classDoc.data().students || [];
  
        let studentsSnapshotDocs = [];
        for (let i = 0; i < studentIds.length; i += 30) {
          const chunk = studentIds.slice(i, i + 30);
          const studentsQuery = query(
            collection(db, 'students'),
            where('__name__', 'in', chunk)
          );
          const chunkSnapshot = await getDocs(studentsQuery);
          studentsSnapshotDocs.push(...chunkSnapshot.docs);
        }
  
        const initialEditableSkills = {};
        const studentsData = studentsSnapshotDocs.map(doc => {
          const studentData = doc.data();
          const enrollment = studentData.enrollments?.find(e => e.classId === classId);
          const psychomotorSkills = enrollment?.psychomotorSkills || {};
  
          // Initialize editable skills
          initialEditableSkills[doc.id] = {};
          psychomotorSkillsList.forEach(skill => {
            initialEditableSkills[doc.id][skill] = psychomotorSkills[skill] || '';
          });
  
          return {
            id: doc.id,
            ...studentData,
            psychomotorSkills
          };
        });
  
        setEditableSkills(initialEditableSkills);
        setStudents(studentsData);
      } catch (error) {
        setError("Error fetching data: " + error.message);
        setShowModal(true);
      } finally {
        setLoading(false);
      }
    };
  
    fetchData();
  }, [classId]);  

  const handleSkillChange = (studentId, skill, value) => {
    setEditableSkills(prev => ({
      ...prev,
      [studentId]: {
        ...prev[studentId],
        [skill]: value
      }
    }));
    setIsDirty(true);
  };

  const saveAllSkills = async () => {
    if (!isDirty) return;
  
    setSaving(true);
    try {
      const batch = writeBatch(db);
  
      students.forEach(student => {
        const studentRef = doc(db, 'students', student.id);
        const enrollments = student.enrollments || [];
        const updatedEnrollments = enrollments.map(enrollment => {
          if (enrollment.classId === classId) {
            return {
              ...enrollment,
              psychomotorSkills: editableSkills[student.id] || {}
            };
          }
          return enrollment;
        });
        batch.update(studentRef, { enrollments: updatedEnrollments });
      });
  
      await batch.commit();
  
      // Update local state
      const updatedStudents = students.map(student => ({
        ...student,
        psychomotorSkills: editableSkills[student.id] || {}
      }));
      
      setStudents(updatedStudents);
      setIsDirty(false);
      setSuccessMessage("Psychomotor skills saved successfully!");
      setShowModal(true);
    } catch (error) {
      setError("Failed to save skills. Please try again.");
      setShowModal(true);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <Loader text={"LOADING"} />;
  }

  return (
    <>
      <div className="scroll-fixed" style={{minHeight: '100vh'}}>
        <div className="top-elements row align-center space-between">
          <Link to="/home" className="back-link">
            <i className="fa-solid fa-arrow-left size3"></i>
          </Link>
          <div className="column align-center justify-center">
            <img src={Logo} className="middle-logo" alt="logo" />
          </div>
          <Link to="#" className="home-link" onClick={() => window.location.reload()}>
            <i className="fa-solid fa-rotate-right size4"></i>
          </Link>
        </div>
        <div style={{ maxWidth: '100vw', padding: '20px', marginTop: '80px' }}>
          <p className="caps male-female-stat">Psychomotor Skills Assessment</p>
          <p className="bold male-female-stat">{classInfo?.name} ({searchParams.get('sessionName')} - {searchParams.get('termName')} Term)</p>      
          
          <div className="scroll-fixed scroll-fixed1" style={{ overflowX: 'auto', width: '100%' }}>
            <table style={{ 
              width: '100%',
              borderCollapse: 'collapse',
              fontSize: '0.9rem'
            }}>
              <thead>
                <tr className="fees-table" style={{ backgroundColor: '#f2f2f2' }}>
                  <th style={{ padding: '4px', border: '1px solid #ddd', textAlign: 'center', width: '40px' }}>S/N</th>
                  <th style={{ padding: '4px', border: '1px solid #ddd', textAlign: 'left', minWidth: '120px' }}>Student</th>
                  {psychomotorSkillsList.map(skill => (
                    <th key={skill} style={{ padding: '4px', border: '1px solid #ddd', textAlign: 'center' }}>
                      {skill.charAt(0).toUpperCase() + skill.slice(1).replace(/([A-Z])/g, ' $1')}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {students.map((student, index) => (
                  <tr className='male-female-stat' key={student.id} style={{ borderBottom: '1px solid #ddd' }}>
                    <td style={{ padding: '4px', textAlign: 'center' }}>{index + 1}</td>
                    <td style={{ padding: '4px' }}>{student.firstName} {student.lastName}</td>
                    {psychomotorSkillsList.map(skill => (
                      <td key={`${student.id}-${skill}`} style={{ padding: '4px', textAlign: 'center' }}>
                        <select
                          className="score-input male-female-stat"
                          value={editableSkills[student.id]?.[skill] || 'A'}
                          onChange={(e) => handleSkillChange(student.id, skill, e.target.value)}
                          style={{ 
                            width: '60px', 
                            height: '30px', 
                            textAlign: 'center',
                            padding: '2px',
                            border: '1px solid #ddd',
                            borderRadius: '4px'
                          }}
                        >
                          <option value="A">A</option>
                          <option value="B">B</option>
                          <option value="C">C</option>
                          <option value="D">D</option>
                          <option value="E">E</option>
                        </select>
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>                  
        </div>
        <div style={{ marginTop: '-10px', marginLeft: '20px', textAlign: 'left' }}>
          <button
            onClick={saveAllSkills}
            disabled={saving || !isDirty}
            className='male-female-stat'
            style={{
              padding: '8px 16px',
              backgroundColor: isDirty ? 'rgb(224, 193, 107)' : '#cccccc',
              color: isDirty ? '#111d2b' : '#111d2bad',
              border: 'none',
              borderRadius: '4px',
              cursor: isDirty ? 'pointer' : 'not-allowed'
            }}
          >
            {saving ? 'Saving Grades . . .' : 'Save All Grades'}
          </button>
          {isDirty && <span className="size5 male-female-stat" style={{ marginLeft: '10px', color: '#666' }}>You have unsaved changes</span>}
        </div>
      </div>

      {showModal && (
        <SuccessModal
          icon={error ? "xmark" : "check"}
          iconColor={error ? "red" : "green"}
          message={error || successMessage}
          onClose={() => {
            setError("");
            setSuccessMessage("");
            setShowModal(false);
          }}
        />
      )}
    </>
  );
};

export default PsychomotorSkills;